import { useRef } from 'react';
import { DefaultBankAccountRequest } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import useFetchDefaultOperationalBankAccount from '@/hooks/useApi/useFetchDefaultOperationalBankAccount';

/**
 * The bank account a schedule pays out to: the default operational account in its currency. With client accounts
 * enabled, the API pays out to the primary client account's default operational account instead, so look that up.
 */
export default function useFetchPayoutAccount() {
  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const { fetchClientAccounts } = useClientAccounts();
  const { operationalBankAccount, fetchDefaultOperationalBankAccount } = useFetchDefaultOperationalBankAccount();
  // Shared by the per-currency lookups a page runs together, so the client account list is fetched once.
  const primaryClientAccountID = useRef<Promise<string | undefined>>(undefined);

  async function fetchPrimaryClientAccountID() {
    const res = await fetchClientAccounts({ ClientType: '' });

    return Object.values(res.ClientAccounts).find((clientAccount) => clientAccount.IsPrimaryAccount === '1')
      ?.ClientAccountID;
  }

  async function fetchPayoutAccount(params: DefaultBankAccountRequest) {
    if (!clientAccountsEnabled) return fetchDefaultOperationalBankAccount(params);

    // Store the request, not its result, so calls that run at the same time all wait on this one fetch.
    if (!primaryClientAccountID.current) {
      primaryClientAccountID.current = fetchPrimaryClientAccountID();
    }

    return fetchDefaultOperationalBankAccount({ ...params, ClientAccountID: await primaryClientAccountID.current });
  }

  return { payoutAccount: operationalBankAccount, fetchPayoutAccount };
}
